import * as turf from '@turf/turf';
import * as geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';

import ConnectedMap from 'app/components/ConnectedMap';
import {RenderMapCallbackOpts} from 'app/components/ConnectedMap/view';
import DrawLayer from 'app/components/DeclarativeMap/DrawLayer';
import FeatureCollectionVector from 'app/components/DeclarativeMap/FeatureCollectionVector';
import FeatureRaster from 'app/components/DeclarativeMap/FeatureRaster';
import GeoJsonFitter from 'app/components/DeclarativeMap/GeoJsonFitter';
import MapContent from 'app/components/DeclarativeMap/MapContent';
import MapStyle from 'app/components/DeclarativeMap/MapStyle';
import {ApiFeature} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization} from 'app/modules/Remote/Organization';
import {ApiProject} from 'app/modules/Remote/Project';
import {WithMonitoringFeatures} from 'app/pages/ProjectDashboardSwitch/view';
import {useFeatureDataForFeature} from 'app/providers/FeatureDataProvider';
import colors from 'app/styles/colors.json';
import * as featureCollectionUtils from 'app/utils/featureCollectionUtils';
import * as geoJsonUtils from 'app/utils/geoJsonUtils';
import * as mapUtils from 'app/utils/mapUtils';
import {useClickableFeatures} from 'app/utils/mapUtils';
import * as makeLayers from 'app/utils/mapUtils/makeLayers';

interface ObservationMapProps {
  imageRefs: mapUtils.MapImageRefs;
  selectedFeature: I.ImmutableOf<ApiFeature>;
  selectedFeatureCollection: I.ImmutableOf<ApiFeatureCollection>;
  organization: I.ImmutableOf<ApiOrganization>;
  selectedProject: I.ImmutableOf<ApiProject>;
  selectedFeatureIdParams: I.Set<string>;
  refreshFeatureCollection: (featureCollectionId: number) => Promise<void> | void;
  noteGeometry: geojson.Geometry;
  setNoteGeometry: (geometry: geojson.Geometry) => void;
  firebaseToken: string;
  allowEditingGeometry: boolean;
  setAllowEditingGeometry: React.Dispatch<React.SetStateAction<boolean>>;
  showNoteGeometry: boolean;
}

export const ObservationMap = ({
  imageRefs,
  selectedFeature,
  selectedFeatureCollection,
  organization,
  selectedProject,
  selectedFeatureIdParams,
  refreshFeatureCollection,
  noteGeometry,
  allowEditingGeometry,
  setAllowEditingGeometry,
  setNoteGeometry,
  firebaseToken,
  showNoteGeometry,
}: ObservationMapProps) => {
  // todo(eva): would be sick to also pass in all notes and have a mode where we
  // can view all note geoms at once and then click on the one we want to see
  const focusedGeometry = React.useMemo(() => {
    return noteGeometry;
  }, [noteGeometry]);

  const fitBoundsOptions = {
    padding: 100,
    maxZoom: 18,
    duration: 1000,
  };
  const featureData = useFeatureDataForFeature(selectedFeature.get('id'));

  return (
    <WithMonitoringFeatures
      selectedProject={selectedProject}
      selectedFeatureCollection={selectedFeatureCollection}
      selectedFeatureIdParams={selectedFeatureIdParams}
      refreshFeatureCollection={refreshFeatureCollection}
    >
      {({selectedFeatures, features}) => {
        const selectedFeatureIds = I.Set([selectedFeature.get('id')]);

        // Create hydrated feature collection
        const hydratedFeatureCollection = featureCollectionUtils.hydratedFeatureCollection(
          selectedFeatureCollection,
          features
        );

        // If the user clicks outside the note geometry, exit editing mode.
        const conditionallyTurnOffEditing = (
          ev: mapboxgl.MapMouseEvent,
          mapInstance: mapboxgl.Map
        ) => {
          if (!allowEditingGeometry) return;

          // // If clicking inside the geometry, keep editing enabled.
          const {lng, lat} = ev.lngLat || {};
          let isInsideGeom = false;
          if (
            noteGeometry &&
            (noteGeometry.type === 'Polygon' || noteGeometry.type === 'MultiPolygon')
          ) {
            isInsideGeom = turf.booleanPointInPolygon([lng, lat], noteGeometry);
          }

          if (isInsideGeom) return;

          // If click isn't on the note geometry layer, exit editing mode.
          const hits = mapInstance.queryRenderedFeatures(ev.point, {
            layers: ['note-geometry-layer', 'note-geometry-layer-fill-click'],
          });
          if (!hits || hits.length === 0) {
            setAllowEditingGeometry(false);
          }
        };

        return (
          <ConnectedMap
            imageRefs={imageRefs}
            featureData={featureData}
            mode={imageRefs.length > 1 ? 'COMPARE' : 'VIEW'}
            firebaseToken={firebaseToken}
            selectedFeatureCollection={hydratedFeatureCollection}
            selectedFeatureIds={selectedFeatureIds}
            selectedFeatures={selectedFeatures}
            organization={organization}
            onMapClick={conditionallyTurnOffEditing}
            controlPosition="top-right"
            disableHotkeys={true}
          >
            {({map, isMapLoaded, cursor, layerKey, isCameraLeader}: RenderMapCallbackOpts) => {
              if (map && organization.get('id') === 'demo') map.scrollZoom.disable();
              return (
                <>
                  <MapStyle map={map} style={mapUtils.MAPBOX_DARK_STYLE} />
                  <FeatureRaster
                    map={map}
                    isMapLoaded={isMapLoaded}
                    activeLayerKey={layerKey}
                    cursor={cursor}
                    featureCollection={hydratedFeatureCollection}
                    featureData={featureData || I.List([])}
                  />
                  {/* Camera leader only; the compare before-map follows via
                      CompareMap's one-directional sync. */}
                  {isCameraLeader && (
                    <GeoJsonFitter
                      map={map}
                      geoJson={focusedGeometry}
                      fitBoundsOptions={fitBoundsOptions}
                    />
                  )}
                  {showNoteGeometry && (
                    <NoteGeometryRenderer
                      map={map}
                      isMapLoaded={isMapLoaded}
                      geometry={noteGeometry}
                      onClick={() => setAllowEditingGeometry(true)}
                    />
                  )}
                  {allowEditingGeometry && (
                    <DrawLayer
                      map={map}
                      isMapLoaded={isMapLoaded}
                      visible={true}
                      feature={
                        noteGeometry
                          ? geoJsonUtils.feature(
                              noteGeometry,
                              {},
                              {id: 'pending-note-location-feature'}
                            )
                          : null
                      }
                      setFeature={(f) => {
                        setNoteGeometry(f?.geometry as geojson.Geometry);
                      }}
                      // in almost all cases we want to draw/edit a rectangle, but if we get back a MultiPolygon
                      // our rectangle mode will error and we should use the polygon editor instead
                      drawingMode={
                        noteGeometry.type === 'Polygon' ? 'draw_rectangle' : 'draw_polygon'
                      }
                      editRectangle={noteGeometry.type === 'Polygon'}
                    />
                  )}
                  {map && (
                    <FeatureCollectionVector
                      map={map}
                      isMapLoaded={isMapLoaded}
                      featureCollection={hydratedFeatureCollection}
                      selectedFeatureIds={selectedFeatureIds}
                      // we're viewing this out of the context of the full feature collection
                      selectFeatureById={() => {}}
                      hoveredFeatureIds={I.Set([])}
                      hoverFeatureById={() => {}}
                      showPropertyBoundaries={true}
                      mode={'focus'}
                    />
                  )}
                </>
              );
            }}
          </ConnectedMap>
        );
      }}
    </WithMonitoringFeatures>
  );
};

// this is a very basic version of how we render note geoms on MonitorProjectView
// 'cause we dont need to haul around all the notes state for existing notes
const NoteGeometryRenderer: React.FC<{
  map: mapboxgl.Map;
  isMapLoaded: boolean;
  geometry: geojson.Geometry;
  onClick: () => void;
}> = ({map, isMapLoaded, geometry, onClick}) => {
  const sourceId = 'note-geometry-source';
  const layerId = 'note-geometry-layer';

  const source = React.useMemo(
    () => ({
      id: sourceId,
      source: {
        type: 'geojson' as const,
        data: geoJsonUtils.feature(geometry, {}),
      },
    }),
    [geometry]
  );

  const layer = React.useMemo(
    () => ({
      id: layerId,
      maxzoom: 22,
      minzoom: 2,
      ...makeLayers.makeLine({
        color: colors.purple,
        width: 4,
        opacity: 1,
      }),
      source: sourceId,
    }),
    [sourceId]
  );

  // Add an almost-transparent fill layer beneath the line to capture clicks
  // when the user clicks inside the geometry (not just on the outline).
  const fillLayer = React.useMemo(
    () => ({
      id: `${layerId}-fill-click`,
      maxzoom: 22,
      minzoom: 2,
      ...makeLayers.makeFill({
        opacity: 0.0,
      }),
      source: sourceId,
    }),
    [layerId, sourceId]
  );

  useClickableFeatures(map, [fillLayer, layer], onClick);

  return (
    <MapContent
      map={map}
      isMapLoaded={isMapLoaded}
      sources={[source]}
      // Render fill first so the outline is on top visually
      layers={[fillLayer, layer]}
    />
  );
};
